import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, ArrowRight, Smartphone, Monitor, X } from "lucide-react";
import { useData } from "@/contexts/DataContext";

const ACK_KEY = "dh_tier_change_acknowledged_at";

type Direction = "up" | "down" | null;

export function TierChangeModal() {
  const { plan } = useData();
  const [, setLocation] = useLocation();
  const prevTierRef = useRef<string | null>(null);
  const [direction, setDirection] = useState<Direction>(null);

  useEffect(() => {
    const cur = plan?.tier ?? null;
    const prev = prevTierRef.current;

    // First settle — capture, no transition fired.
    if (prev === null && cur) {
      prevTierRef.current = cur;
      return;
    }

    if (!prev || !cur || prev === cur) {
      prevTierRef.current = cur;
      return;
    }

    // Transitions of interest only:
    if (prev === "ultimate" && cur === "pro") {
      setDirection("down");
    } else if (prev === "pro" && cur === "ultimate") {
      setDirection("up");
    } else if (prev !== "ultimate" && cur === "ultimate") {
      setDirection("up");
    }

    prevTierRef.current = cur;
  }, [plan?.tier]);

  const dismiss = () => {
    try {
      localStorage.setItem(ACK_KEY, new Date().toISOString());
    } catch {}
    setDirection(null);
  };

  if (!direction) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="tier-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[180] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      >
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="relative w-full max-w-sm rounded-3xl bg-[#1A1614] border border-[#454545] p-6 text-center"
        >
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 rounded-full p-1.5 hover:bg-[#1F1B19]"
          >
            <X className="h-4 w-4 text-[#8A8A92]" />
          </button>

          <div className="flex justify-center mb-4">
            <div
              className={`rounded-2xl p-4 border ${
                direction === "up"
                  ? "bg-[#D4AF37]/15 border-[#D4AF37]/30"
                  : "bg-[#8B1A1A]/15 border-[#8B1A1A]/40"
              }`}
            >
              <Crown
                className={`h-10 w-10 ${
                  direction === "up" ? "text-[#D4AF37]" : "text-[#8B1A1A]"
                }`}
                strokeWidth={1.5}
              />
            </div>
          </div>

          {direction === "up" ? (
            <>
              <h2 className="text-2xl font-black uppercase tracking-tighter text-[#EEEAE3] leading-tight mb-2">
                Welcome to <span className="text-[#D4AF37]">Ultimate</span>
              </h2>
              <p className="text-sm text-[#8A8A92] leading-relaxed mb-5">
                Install DailyHisaab Desktop to unlock full POS billing. Use this phone for reports
                and managing staff access.
              </p>
              <div className="space-y-2.5 mb-5 text-left">
                <div className="flex items-center gap-3 rounded-xl border border-[#454545] bg-[#1F1B19]/60 p-3">
                  <Monitor className="h-4 w-4 text-[#D4AF37]" />
                  <span className="text-xs text-[#EEEAE3]">2 desktop seats included</span>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-[#454545] bg-[#1F1B19]/60 p-3">
                  <Smartphone className="h-4 w-4 text-[#D4AF37]" />
                  <span className="text-xs text-[#EEEAE3]">Mobile becomes admin console</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-black uppercase tracking-tighter text-[#EEEAE3] leading-tight mb-2">
                Welcome back to <span className="text-[#8B1A1A]">Pro</span>
              </h2>
              <p className="text-sm text-[#8A8A92] leading-relaxed mb-5">
                Your mobile is now your primary billing tool. Desktop access has ended; staff
                devices have been signed out.
              </p>
            </>
          )}

          <button
            onClick={dismiss}
            className="w-full rounded-2xl bg-[#8B1A1A] hover:bg-[#631212] py-3.5 px-6 flex items-center justify-center gap-2 text-sm font-black uppercase tracking-wider text-white transition-colors"
          >
            {direction === "up" ? "Continue to Admin Console" : "Continue to Pro View"}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
